import React, { createContext, useContext, useEffect, useState } from 'react';

interface ThemeContextType {
    isDarkMode: boolean;
    toggleTheme: () => void;
    setDarkMode: (dark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'theme';
const SYSTEM_DARK = '(prefers-color-scheme: dark)';

// NFR-UX-1: the app follows the system until the user picks Light or Dark, and only that choice is remembered in the
// browser: nothing is stored for someone who never chose (docs/GDPR.md a14).
function storedChoice(): boolean | null {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved === 'dark' ? true : saved === 'light' ? false : null;
    } catch {
        return null;
    }
}

function systemDark(): boolean {
    return window.matchMedia(SYSTEM_DARK).matches;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [choice, setChoice] = useState<boolean | null>(storedChoice);
    const [systemIsDark, setSystemIsDark] = useState(systemDark);
    const isDarkMode = choice ?? systemIsDark;

    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDarkMode);
    }, [isDarkMode]);

    useEffect(() => {
        if (choice !== null) return;
        const query = window.matchMedia(SYSTEM_DARK);
        const follow = (event: MediaQueryListEvent) => setSystemIsDark(event.matches);
        query.addEventListener('change', follow);
        return () => query.removeEventListener('change', follow);
    }, [choice]);

    const setDarkMode = (dark: boolean) => {
        setChoice(dark);
        try {
            localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
        } catch {
            // Storage refused (private mode): the choice holds until the page is reloaded.
        }
    };

    const toggleTheme = () => setDarkMode(!isDarkMode);

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleTheme, setDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
